import { ProductType } from '~/core/enums';
import { Estimate } from '~/core/models';
import { SelectedCustomCutouts } from '~/core/types/appliances';
import { convertProductsToProductDetailsList } from '~/core/utils';

export const convertCutoutsToProductDetails =
  convertProductsToProductDetailsList(ProductType.CUTOUT);

interface CutoutOnShape {
  [id: number]: { quantity: number };
}

/**
 * Extracts and counts the cutouts from the given drawing shapes.
 *
 * @param {Estimate} param - The estimate object containing the drawing shapes.
 * @returns {CutoutOnShape} A record where the keys are appliance product IDs, and the values represent their quantities.
 */
export const getCutoutsFromShapes = ({ drawingShapes }: Estimate) =>
  drawingShapes
    ?.flatMap((shape) =>
      shape.appliances.map((appliance) => appliance.applianceModel.productId)
    )
    .reduce<CutoutOnShape>((acc, id) => {
      if (acc[id]) {
        acc[id].quantity += 1;
      } else {
        acc[id] = { quantity: 1 };
      }

      return acc;
    }, {});

/**
 * Extracts and counts the cutouts from the estimate's cutOuts object and formats them into a selected custom cutouts structure.
 *
 * @param {Estimate} param - The estimate object containing cutOuts.
 * @returns {SelectedCustomCutouts} The selected custom cutouts structure, with each custom cutout having an `onCard` quantity.
 */
export const getCutoutsFromEstimate = ({ cutOuts }: Estimate) =>
  Object.entries(cutOuts).reduce<SelectedCustomCutouts>(
    (acc, [id, value]) => ({
      selected: {
        ...acc.selected,
        [id]: {
          onCard: value.quantity,
        },
      },
    }),
    { selected: {} }
  );

/**
 * Merges two cutout structures (`cutoutsOnCards` and `cutoutsOnShapes`) into a single object.
 * This function combines the `onCard` quantity from `cutoutsOnCards` and the `onShape` quantity from `cutoutsOnShapes`,
 * calculating the difference between them for the final `onCard` value and combining them into a unified object.
 *
 * @param {SelectedCustomCutouts} cutoutsOnCards - A record of selected cutouts, where each key represents an appliance ID and `onCard` represents the quantity on the card.
 * @param {CutoutOnShape} cutoutsOnShapes - A record where the key is the appliance ID and the value is the quantity on the shape.
 * @returns {Object} An object containing a `selected` key, which is a record of appliance IDs with both `onCard` and `onShape` quantities.
 */
export const mergeAllCutouts = (
  cutoutsOnCards: SelectedCustomCutouts,
  cutoutsOnShapes?: CutoutOnShape
) => ({
  selected: Object.keys(cutoutsOnCards.selected).reduce<{
    [id: number]: {
      onCard: number;
      onShape: number;
    };
  }>((acc, id) => {
    const numericId = Number(id);
    const cutoutOnShapeQty = cutoutsOnShapes?.[numericId]?.quantity ?? 0;

    acc[numericId] = {
      onCard: cutoutsOnCards.selected[numericId].onCard - cutoutOnShapeQty,
      onShape: cutoutOnShapeQty,
    };

    return acc;
  }, {}),
});
